import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Card,
  CardContent,
  Chip,
  Stack,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Divider,
  Alert,
  CircularProgress,
  IconButton,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Assignment as AssignmentIcon,
  AccessTime as AccessTimeIcon,
  Person as PersonIcon,
  Save as SaveIcon,
  Event as EventIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { OnboardingSession, OnboardingUserProgress } from '../../../types/onboarding.types';
import { apiService } from '../../../services/api';
import CreateSessionsModal from './CreateSessionsModal';

interface UserOnboardingModalProps {
  open: boolean;
  onClose: () => void;
  userProgress: OnboardingUserProgress | null;
}

interface SessionUpdate {
  sessionCompleted: boolean;
  confidenceLevel: number | undefined;
  lineManagerFeedback: string;
}

const UserOnboardingModal: React.FC<UserOnboardingModalProps> = ({
  open,
  onClose,
  userProgress,
}) => {
  const [sessions, setSessions] = useState<OnboardingSession[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [updates, setUpdates] = useState<Record<number, SessionUpdate>>({});
  const [hideCompleted, setHideCompleted] = useState(false);
  const [createSessionsModalOpen, setCreateSessionsModalOpen] = useState(false);

  useEffect(() => {
    if (open && userProgress) {
      loadUserSessions();
    }
  }, [open, userProgress]);

  const loadUserSessions = async () => {
    if (!userProgress) return;

    try {
      setLoading(true);
      setError(null);
      const data = await apiService.getOnboardingUserSessions(userProgress.userId);
      // Sort by start date
      const sortedSessions = data.sort((a, b) => 
        new Date(a.startDateTime).getTime() - new Date(b.startDateTime).getTime()
      );
      setSessions(sortedSessions);
      
      // Initialize updates state
      const initialUpdates: Record<number, SessionUpdate> = {};
      sortedSessions.forEach(session => {
        initialUpdates[session.id] = {
          sessionCompleted: session.sessionCompleted,
          confidenceLevel: session.confidenceLevel,
          lineManagerFeedback: session.lineManagerFeedback || '',
        };
      });
      setUpdates(initialUpdates);
    } catch (err) {
      console.error('Error loading user sessions:', err);
      setError('Failed to load user sessions');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateChange = (sessionId: number, field: keyof SessionUpdate, value: any) => {
    setUpdates(prev => ({
      ...prev,
      [sessionId]: {
        ...prev[sessionId],
        [field]: value,
      },
    }));
  };

  const handleSaveSession = async (sessionId: number) => {
    const session = sessions.find(s => s.id === sessionId);
    const update = updates[sessionId];
    
    if (!session || !update) return;

    try {
      setSaving(true);
      
      const updateDto = {
        id: sessionId,
        onboardUserId: session.onboardUserId,
        topic: session.topic,
        description: session.description,
        startDateTime: session.startDateTime,
        endDateTime: session.endDateTime,
        duration: session.duration,
        presenter: session.presenter,
        presenterEmail: session.presenterEmail,
        employeeEmail: session.employeeEmail,
        otherAttendeeEmails: session.otherAttendeeEmails,
        resourceLinks: session.resourceLinks,
        sessionCompleted: update.sessionCompleted,
        confidenceLevel: update.confidenceLevel,
        employeeFeedback: session.employeeFeedback,
        lineManagerFeedback: update.lineManagerFeedback,
      };

      const updatedSession = await apiService.updateOnboardingSession(sessionId, updateDto);
      
      if (updatedSession) {
        // Update the local sessions state
        setSessions(prev => prev.map(s => 
          s.id === sessionId ? updatedSession : s
        ));
        setError(null);
      }
    } catch (err) {
      console.error('Error updating session:', err);
      setError('Failed to update session');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSession = async (sessionId: number) => {
    if (!window.confirm('Are you sure you want to delete this session? This action cannot be undone.')) {
      return;
    }

    try {
      setDeleting(sessionId);
      setError(null);
      
      const success = await apiService.deleteOnboardingSession(sessionId);
      
      if (success) {
        // Remove the session from local state
        setSessions(prev => prev.filter(s => s.id !== sessionId));
        // Remove from updates state
        setUpdates(prev => {
          const newUpdates = { ...prev };
          delete newUpdates[sessionId];
          return newUpdates;
        });
        setError(null);
      }
    } catch (err) {
      console.error('Error deleting session:', err);
      setError('Failed to delete session');
    } finally {
      setDeleting(null);
    }
  };

  const getStatusColor = (completed: boolean): 'success' | 'warning' => {
    return completed ? 'success' : 'warning';
  };

  const getStatusIcon = (completed: boolean) => {
    return completed ? <CheckCircleIcon color="success" sx={{ fontSize: 20 }} /> : <ScheduleIcon color="warning" sx={{ fontSize: 20 }} />;
  };

  const formatDateTime = (dateTime: string) => {
    return new Date(dateTime).toLocaleString();
  };

  const hasChanges = (sessionId: number) => {
    const session = sessions.find(s => s.id === sessionId);
    const update = updates[sessionId];
    
    if (!session || !update) return false;
    
    return (
      session.sessionCompleted !== update.sessionCompleted ||
      session.confidenceLevel !== update.confidenceLevel ||
      (session.lineManagerFeedback || '') !== update.lineManagerFeedback
    );
  };

  const handleCreateSessions = () => {
    setCreateSessionsModalOpen(true);
  };

  const handleCreateSessionsModalClose = () => {
    setCreateSessionsModalOpen(false);
  };

  const handleSessionsCreated = () => {
    // Refresh the sessions data when new sessions are created
    loadUserSessions();
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="lg" 
      fullWidth
      PaperProps={{
        sx: { minHeight: '80vh' }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <PersonIcon color="primary" />
          <Box>
            <Typography variant="h6">
              {userProgress?.firstName} {userProgress?.lastName} - Onboarding Sessions
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {userProgress?.completedSessions}/{userProgress?.totalSessions} sessions completed ({userProgress?.completionPercentage}%)
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : (
          <>
            {/* Filter Controls */}
            <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={hideCompleted}
                    onChange={(e) => setHideCompleted(e.target.checked)}
                    color="primary"
                  />
                }
                label="Hide Completed Sessions"
              />
            </Box>

            <Stack spacing={2}>
              {sessions
                .filter(session => {
                  const update = updates[session.id];
                  if (!update) return false;
                  // Use original session data for filtering, not local updates
                  if (hideCompleted && session.sessionCompleted) return false;
                  return true;
                })
                .map((session) => {
                  const update = updates[session.id];
                  if (!update) return null;

                  return (
                <Card key={session.id} variant="outlined">
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <AssignmentIcon color="primary" />
                      <Typography variant="h6" sx={{ flexGrow: 1 }}>
                        {session.topic}
                      </Typography>
                      <Chip
                        icon={getStatusIcon(update.sessionCompleted)}
                        label={update.sessionCompleted ? 'Completed' : 'Pending'}
                        color={getStatusColor(update.sessionCompleted)}
                        variant="outlined"
                        size="small"
                      />
                      <IconButton
                        onClick={() => handleDeleteSession(session.id)}
                        disabled={deleting === session.id}
                        size="small"
                        color="error"
                        sx={{ ml: 1 }}
                      >
                        {deleting === session.id ? (
                          <CircularProgress size={20} />
                        ) : (
                          <DeleteIcon />
                        )}
                      </IconButton>
                    </Box>

                    {session.description && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {session.description}
                      </Typography>
                    )}

                    <Stack spacing={1} sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <AccessTimeIcon sx={{ fontSize: 16 }} color="action" />
                        <Typography variant="body2" color="text.secondary">
                          {formatDateTime(session.startDateTime)} - {formatDateTime(session.endDateTime)}
                        </Typography>
                      </Box>
                      
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PersonIcon sx={{ fontSize: 16 }} color="action" />
                        <Typography variant="body2" color="text.secondary">
                          Presenter: {session.presenter}
                        </Typography>
                      </Box>

                      <Typography variant="body2" color="text.secondary">
                        Duration: {session.duration} hours
                      </Typography>
                    </Stack>

                    <Divider sx={{ my: 2 }} />

                    {/* Management Controls */}
                    <Typography variant="subtitle2" gutterBottom>
                      Management Controls
                    </Typography>
                    
                    <Stack spacing={2}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={update.sessionCompleted}
                            onChange={(e) => handleUpdateChange(session.id, 'sessionCompleted', e.target.checked)}
                          />
                        }
                        label="Session Completed"
                      />

                      <FormControl size="small" sx={{ minWidth: 200 }}>
                        <InputLabel>Confidence Level</InputLabel>
                        <Select
                          value={update.confidenceLevel || ''}
                          label="Confidence Level"
                          onChange={(e) => handleUpdateChange(session.id, 'confidenceLevel', e.target.value ? Number(e.target.value) : null)}
                        >
                          <MenuItem value="">Not Set</MenuItem>
                          <MenuItem value={1}>1 - Very Low</MenuItem>
                          <MenuItem value={2}>2 - Low</MenuItem>
                          <MenuItem value={3}>3 - Medium</MenuItem>
                          <MenuItem value={4}>4 - High</MenuItem>
                          <MenuItem value={5}>5 - Very High</MenuItem>
                        </Select>
                      </FormControl>

                      <TextField
                        label="Line Manager Feedback"
                        multiline
                        rows={3}
                        value={update.lineManagerFeedback}
                        onChange={(e) => handleUpdateChange(session.id, 'lineManagerFeedback', e.target.value)}
                        placeholder="Enter feedback for this session..."
                        size="small"
                      />

                      {session.employeeFeedback && (
                        <Box>
                          <Typography variant="subtitle2" gutterBottom>
                            Employee Feedback
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                            "{session.employeeFeedback}"
                          </Typography>
                        </Box>
                      )}

                      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                          variant={hasChanges(session.id) ? "contained" : "outlined"}
                          size="small"
                          startIcon={<SaveIcon />}
                          onClick={() => handleSaveSession(session.id)}
                          disabled={!hasChanges(session.id) || saving}
                          sx={{
                            ...(hasChanges(session.id) 
                              ? {
                                  backgroundColor: 'primary.main',
                                  color: 'white',
                                  '&:hover': {
                                    backgroundColor: 'primary.dark',
                                  }
                                }
                              : {
                                  backgroundColor: 'grey.100',
                                  color: 'grey.500',
                                  borderColor: 'grey.300',
                                  '&:hover': {
                                    backgroundColor: 'grey.200',
                                  }
                                }
                            )
                          }}
                        >
                          {saving ? 'Saving...' : hasChanges(session.id) ? 'Save Changes' : 'No Changes'}
                        </Button>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              );
                })}
            </Stack>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button 
          variant="contained"
          startIcon={<EventIcon />}
          onClick={handleCreateSessions}
          disabled={!userProgress}
        >
          Create Sessions
        </Button>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>

      {/* Create Sessions Modal */}
      {userProgress && (
        <CreateSessionsModal
          open={createSessionsModalOpen}
          onClose={handleCreateSessionsModalClose}
          userId={userProgress.userId}
          userName={`${userProgress.firstName} ${userProgress.lastName}`}
          onSessionsCreated={handleSessionsCreated}
        />
      )}
    </Dialog>
  );
};

export default UserOnboardingModal;
